<template>
    <PlanoScaffold :datas="template">
        <PlanoHeader :logoUrl="logoUrl">
            <fragment slot="trailing">
                <OutlineButton @click="save">save</OutlineButton>
                <OutlineButton @click="exit();">{{ $t("plano.editor.header.file.exit") }}</OutlineButton>
            </fragment>
        </PlanoHeader>
        <PlanoNav @save="save" @saveAs="toggleModalSaveAs(true);" exitTo="/displayers"></PlanoNav>
        <TemplateForm v-if="!loading" ref="form" v-model="template"></TemplateForm>
        <hr v-if="!loading" />
        <PlanoEngine
            v-if="!loading"
            :key="templateId || 0"
            :catalogPanes="catalogPanes"
            :catalogItems="fullCatalog"
            :systemType="template.systemType.id"
            @search-changed="searchChanged"
        ></PlanoEngine>
        <ModalSaveAsTemplate v-if="!loading && showModalSaveAs" :template="datasTosave" @close="toggleModalSaveAs(false);"></ModalSaveAsTemplate>
        <ModalExitPlano
            v-if="showExitModal"
            @save="saveAfterExit($event);"
            @close="closeExitModal();"
            :name="template && template.name"
            :readOnly="readOnly"
        ></ModalExitPlano>
        <Loader v-else :loading="loading"></Loader>
    </PlanoScaffold>
</template>

<script>
import PlanoHeader from "./plano-header";
import PlanoNav from "./plano-nav";
import PlanoScaffold from "./plano-scaffold";
import PlanoEngine from "./plano-engine";
import OutlineButton from "./components/outline-button";
import TemplateForm from "./template-form";
import { Context, Tools, save, load } from "plano_engine";
import displayer from "@/services/displayer";
import Loader from "@/components/loader/loader";
import Globals from "@/components/globals";
import ModalBase from "@/components/modal/ModalBase";
import ModalSaveAsTemplate from "./modal/ModalSaveAsTemplate";
import { allPane, furniturePane, modulesPane, structurePane, centerCarcass, blankingGraphicPane } from "./utils";
import ModalExitPlano from "./modal/ModalExitPlano";
import componentService, { PLANO_STEP } from "@/services/component";
import shadeService from "@/services/shades";
import ExitPlanoBuilderMixin from "@/mixins/ExitPlanoBuilderMixin";
import cacheService from "@/services/cache";
import {
    PaginationMixin,
    findAllComponentsSpecByType,
    COMPONENT_TYPE,
    getAllModuleIdFromModuleElements,
    getAllShadeIdForModuleElements,
    injectElementDetailsInModels
} from "./utils";
import { renderTrads } from "./utils/parse-catalog";

const paneModel1 = initialLocale => ({
    label: renderTrads({
        names: {
            en: "Pane",
            fr: "PaneFR"
        },
        initialLocale
    }),
    builder: () => BoardPane.create(2000, 1000, 0, 0, 1990, 990, 50, 50, true, true)
});

const fixing = initialLocale => ({
    label: renderTrads({
        names: {
            en: "Caddy fixing",
            fr: "Accroche de Caddy"
        },
        initialLocale,
        trads: {}
    }),
    builder: () => BoardFixing.create()
});

const PLANO_TYPE = {
    shelf: 2,
    caddy: 3,
    tile: 4
};

export default {
    mixins: [PaginationMixin, ExitPlanoBuilderMixin],
    components: {
        Loader,
        PlanoHeader,
        PlanoNav,
        PlanoScaffold,
        PlanoEngine,
        OutlineButton,
        TemplateForm,
        ModalBase,
        ModalSaveAsTemplate,
        ModalExitPlano
    },
    data() {
        return {
            loading: false,
            template: {
                id: undefined,
                brand: { id: undefined },
                campaign: { id: undefined },
                zones: [],
                systemType: {
                    id: Number.parseInt(this.$route.query.type, 10)
                }
            },
            catalogPanes: {
                allPane,
                modulesPane,
                furniturePane,
                blankingGraphicPane,
                structurePane
            },
            catalogItems: {},
            hardCodedItems: {},
            showModalSaveAs: false,
            datasTosave: undefined
        };
    },
    watch: {
        template(template) {
            // delay the load otherwise we're calling load before the engine is ready
            setTimeout(() => {
                load(template.config);
                setTimeout(() => {
                    this.initialPlano = save();
                }, 0);
            }, 0);
        },
        templateId(current, old) {
            if (current.id !== (old && old.id)) this.fetchTemplate();
        },
        zones() {
            this.fillCatalog();
        },
        "template.campaign"() {
            this.fillCatalog();
        }
    },
    created() {
        function parse(uri) {
            return Number.parseInt(cacheService.get(uri), 10);
        }
        const templateId = this.$route.params.id && Number.parseInt(this.$route.params.id, 10);
        if (templateId) {
            this.fetchTemplate(templateId);
        } else {
            this.template = {
                id: undefined,
                brand: { id: parse("currentBrandId") },
                zones: [{ id: parse("currentZoneId") }],
                campaign: { id: parse("currentCampaignId") },
                standDesign: { id: parse("currentStandId") },
                systemType: { id: this.$route.query.type && Number.parseInt(this.$route.query.type, 10) }
            };
        }
    },
    computed: {
        logoUrl() {
            const brands = cacheService.getBrands();
            const brandId = Number.parseInt(cacheService.getCurrentBrandId(), 10);

            const brand = brands.find(brand => brand.id === brandId);

            return `${Globals.getBaseUrl()}${brand.imgPath}${brand.logo}`;
        },
        templateId() {
            return this.template && this.template.id;
        },
        isEditing() {
            return this.$route.params.id !== undefined;
        },
        zones() {
            return this.template && this.template.zones;
        },
        fullCatalog() {
            return { ...this.hardCodedItems, ...this.catalogItems.value };
        }
    },
    methods: {
        async toggleModalSaveAs(value) {
            if (value === true) {
                try {
                    this.datasTosave = { ...this.$refs.form.submit(), id: this.template.id, systemTypeId: this.template.systemType.id };
                } catch (err) {
                    this.dialogError(err);
                    throw err;
                }
            }
            this.showModalSaveAs = value;
        },
        fillCatalog() {
            switch (this.template.systemType.id) {
                case PLANO_TYPE.shelf:
                    this.hardCodedItems = {
                        paneModel1: paneModel1(this.$locale)
                        // ladderModel: ladderModel(this.$locale),
                        // ladderModel2: ladderModel2(this.$locale)
                    };
                    break;
                case PLANO_TYPE.caddy:
                    this.hardCodedItems = {
                        paneModel1: paneModel1(this.$locale),
                        fixing: fixing(this.$locale)
                    };
                    break;
                case PLANO_TYPE.tile:
                    this.hardCodedItems = {
                        paneModel1: paneModel1(this.$locale)
                    };
            }
            if (this.template.systemType && this.template.brand) {
                this.setupCatalogPagination({
                    step: PLANO_STEP.template,
                    // the user may have opened a template from a different brand/zone than the one currently selected
                    // so we use values from the template instead of local storage.
                    systemTypeId: this.template.systemType.id,
                    brandId: this.template.brand.id,
                    zones: this.zones.map(z => z.id)
                });
            }
        },
        async fetchTemplate(id = undefined) {
            id = id || this.templateId;
            if (!id || (this.template && this.template.id === id)) return;

            this.loading = true;
            try {
                const template = await displayer.get(id);

                const moduleSpecs = findAllComponentsSpecByType(template.config, [COMPONENT_TYPE.module], { includeScratchpad: true });
                const moduleIds = getAllModuleIdFromModuleElements(moduleSpecs);
                const modulesDetails = await componentService.fromIds([...moduleIds], {
                    brandId: template.brand.id,
                    zones: template.zones.map(z => z.id),
                    campaignId: 0
                });
                injectElementDetailsInModels(template.config, modulesDetails);

                this.template = template;
                centerCarcass();
            } finally {
                this.loading = false;
            }
            setTimeout(() => {
                Context.zoomMin();
                if (this.isEditing) {
                    Context.table.paper.setSize(4000 * Context.rescaleFactor, 2400 * Context.rescaleFactor);
                }
            }, 100);
        },
        exit() {
            this.$router.push("/displayers");
        },
        async save() {
            let form;
            try {
                form = this.$refs.form.submit();
            } catch (err) {
                this.dialogError(err);
                throw err;
            }

            let id;
            try {
                const updatedTemplate = {
                    ...form,
                    systemTypeId: this.template.systemType.id,
                    config: save()
                };
                const hasPane = updatedTemplate.config.models.some(model => model.type === "Pane");
                if (!hasPane) {
                    throw new Error(this.$trad.plano.template.errorNoCarcass);
                }

                const response = this.isEditing ? await displayer.update(this.templateId, updatedTemplate) : await displayer.create(updatedTemplate);
                id = response.id;
                this.initialPlano = save();
                this.dialogSuccess(response.message);
            } catch (err) {
                console.error(err);
                this.dialogErrorToast(err && err.message);
                throw err;
            }

            if (!this.isEditing) {
                this.$router.push(`/templates/${id}`);
            } else {
                await this.fetchTemplate(id);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
hr {
    margin-left: 100px;
    margin-bottom: 0;
    width: calc(100% - 200px);
    border-bottom: 1px solid darkgray;
}
</style>



// WEBPACK FOOTER //
// template.vue?b8894ad0